import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useReactlytics } from '@americanexpress/reactlytics';
import { analyticsContext } from '@/context/analyticsContext';
import {
  generateCustomEventInfo,
  generateIdentifier,
} from '@/helpers/analyticsHelpers';
import { getLounge } from '@/selectors/loungeSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { getLocaleData } from '@/selectors/localeSelectors';

const CustomTrackProvider = ({ children }) => {
  const { airportCode, product, loungeId, pathname } = useRouteParams();
  const lounge = useSelector(getLounge);
  const { locale = 'en-us' } = useSelector(getLocaleData);

  const { track } = useReactlytics({
    data: { product: { type: product, description: product, locale } },
  });
  const identifier = generateIdentifier({ airportCode, loungeId, pathname });
  const info = loungeId
    ? `${lounge?.name} at ${airportCode}`
    : airportCode
      ? `${airportCode}`
      : '';

  const customTrack = React.useCallback(
    ({ event, detail, element, identifier: identifierOverride }) => {
      track({
        event: generateCustomEventInfo(
          event,
          { identifier: identifierOverride || identifier, detail, element },
          {
            info,
            products: [
              {
                id: product,
                lineOfBusiness: 'consumers',
                paymentType: 'charge',
                currencyCode: '',
                type: product,
                description: product,
                locale: locale.toLowerCase(),
              },
            ],
          }
        ),
      });
    },
    [identifier, track, product, locale, info]
  );

  const customTrackValue = React.useMemo(
    () => ({
      customTrack,
      identifier,
    }),
    [customTrack, identifier]
  );

  return (
    <analyticsContext.Provider value={customTrackValue}>
      {children}
    </analyticsContext.Provider>
  );
};

CustomTrackProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CustomTrackProvider;
